import type { ReactNode } from "react";
import { SvgIcon } from "./ui/SvgIcon";
import checkIcon from "../assets/check-icon.svg?raw";

interface ArticleActionButtonProps {
  variant?: "primary" | "selected" | "muted" | "link";
  onClick?: () => void;
  disabled?: boolean;
  isDark: boolean;
  testId?: string;
  children: ReactNode;
}

const variantClasses = {
  primary: (isDark: boolean) =>
    `flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-all cursor-pointer ${
      isDark
        ? "bg-white text-black hover:bg-zinc-200 border border-transparent"
        : "bg-gray-900 text-white hover:bg-gray-800 border border-transparent"
    }`,
  selected: (isDark: boolean) =>
    `flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
      isDark
        ? "bg-zinc-800 text-zinc-500 cursor-default border border-zinc-700"
        : "bg-gray-100 text-gray-400 cursor-default border border-gray-200"
    }`,
  muted: (isDark: boolean) =>
    `text-xs px-3 py-1.5 rounded-lg font-medium cursor-pointer ${
      isDark
        ? "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
    }`,
  link: (isDark: boolean) =>
    `text-sm font-medium cursor-pointer transition-colors ${
      isDark
        ? "text-zinc-400 hover:text-white"
        : "text-gray-500 hover:text-gray-900"
    }`,
} as const;

export function ArticleActionButton({
  variant = "primary",
  onClick,
  disabled = false,
  isDark,
  testId,
  children,
}: ArticleActionButtonProps) {
  const isSelected = variant === "selected";

  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled || isSelected}
      className={variantClasses[variant](isDark)}
    >
      <span className={isSelected ? "contents" : "hidden"} aria-hidden="true">
        <SvgIcon markup={checkIcon} size={12} strokeWidth={3} />
      </span>
      {children}
    </button>
  );
}
